import { useParams } from 'react-router-dom'
import { Button, HStack, IconButton, Spinner, Text, VStack, useDisclosure } from '@chakra-ui/react'
import { FiKey, FiPlus, FiRefreshCw } from 'react-icons/fi'
import { Tooltip } from '../../components/ui/tooltip'
import { PageContainer, Card } from '../../components/shared'
import { MintCanvasserModal } from '../../components/campaign/canvassers/MintCanvasserModal'
import { CanvasserRosterTable } from '../../components/campaign/canvassers/CanvasserRosterTable'
import { FieldProfileToggle } from '../../components/campaign/canvassers/FieldProfileToggle'
import { SharedCodeCard } from '../../components/campaign/canvassers/SharedCodeCard'
import { PointOfContactEditor } from '../../components/campaign/canvassers/PointOfContactEditor'
import { ImpactSettingsEditor } from '../../components/campaign/canvassers/ImpactSettingsEditor'
import { useCanvasserRoster } from '../../hooks/useCampaign'
import { useProfile } from '../../hooks/useProfile'
import { extractApiMessage } from '../../lib/apiError'

/**
 * Organizer-facing canvasser panel: mint canvasser access grants, toggle the campaign's default
 * field profile, and manage the roster.
 *
 * L11 slice 1e Task 14 wired the route + the roster data hook; Task 15 wired MintCanvasserModal;
 * Task 16 wires CanvasserRosterTable + FieldProfileToggle below.
 */
export function CampaignCanvassersPage() {
  const { id } = useParams<{ id: string }>()
  const { data: roster, isLoading, error, refetch, isFetching } = useCanvasserRoster(id)
  const mintModal = useDisclosure()
  const selfMintModal = useDisclosure()
  const { data: profile } = useProfile()

  return (
    <PageContainer
      title="Canvassers"
      subtitle="Mint field access for canvassers and manage their roster"
      actions={
        <HStack gap={2}>
          {/* CANV-1: manual refresh only — newly-minted grants and first-time sign-ins don't
              appear until this one grid refetches, and navigating the page doesn't refetch.
              Deliberately NOT an auto-poll: fires on tap, for an organizer watching for a login. */}
          <Tooltip content="Refresh roster">
            <IconButton
              aria-label="Refresh roster"
              variant="ghost"
              onClick={() => refetch()}
              loading={isFetching}
              minH="44px"
              minW="44px"
              _touch={{ minH: '44px', minW: '44px' }}
            >
              <FiRefreshCw />
            </IconButton>
          </Tooltip>
          <Button colorPalette="brand" onClick={mintModal.onOpen} flex="1">
            <FiPlus /> Add canvasser
          </Button>
        </HStack>
      }
    >
      <VStack gap={6} align="stretch">
        {/* [L11-3b §4D] Staff-who-walk: one credential = one mode. A staff member who wants to
            canvass mints themselves a Canvasser grant through the SAME mint path as anyone else
            and opens that link/code — deliberately not a "switch to canvassing" button on the
            organizer JWT, which would reintroduce the dual-credential ambiguity the GrantOnly
            canvassing endpoints removed. No dedupe this slice: repeated taps mint extra grants,
            which are revocable from the roster below. */}
        <Card>
          <HStack justify="space-between" gap={4} flexWrap="wrap">
            <VStack align="start" gap={0}>
              <Text fontWeight="medium">Walking this campaign yourself?</Text>
              <Text fontSize="sm" color="muted">
                Mint yourself a canvasser link and code, then open it on your phone.
              </Text>
            </VStack>
            <Button variant="outline" minH="44px" _touch={{ minH: '44px' }} onClick={selfMintModal.onOpen}>
              <FiKey /> Get my own link/code
            </Button>
          </HStack>
        </Card>

        {id && <FieldProfileToggle campaignId={id} />}

        {id && <PointOfContactEditor campaignId={id} />}

        {id && <ImpactSettingsEditor campaignId={id} />}

        {id && <SharedCodeCard campaignId={id} />}

        {isLoading && (
          <VStack py={12}>
            <Spinner size="lg" color="accent" />
            <Text color="muted">Loading canvassers...</Text>
          </VStack>
        )}

        {!!error && (
          <Card borderColor="danger.fg">
            <Text color="danger.fg">
              {extractApiMessage(error, 'Failed to load canvassers. Please try again.')}
            </Text>
          </Card>
        )}

        {!isLoading && !error && roster && roster.length === 0 && (
          <Card textAlign="center" py={12}>
            <Text color="muted" mb={4}>No canvassers yet</Text>
            <Button colorPalette="brand" onClick={mintModal.onOpen}>
              <FiPlus /> Add your first canvasser
            </Button>
          </Card>
        )}

        {!isLoading && !error && roster && roster.length > 0 && id && (
          <CanvasserRosterTable campaignId={id} rows={roster} />
        )}
        {id && (
          <MintCanvasserModal campaignId={id} isOpen={mintModal.open} onClose={mintModal.onClose} />
        )}
        {id && (
          <MintCanvasserModal
            campaignId={id}
            isOpen={selfMintModal.open}
            onClose={selfMintModal.onClose}
            initialFirstName={profile?.firstName}
            initialLastName={profile?.lastName}
            title="Get my own link/code"
          />
        )}
      </VStack>
    </PageContainer>
  )
}
